import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Grid, Card, Image, Icon, Button } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { FormattedDate, FormattedNumber } from 'react-intl';
import queryString from 'query-string';
import capitalize from 'capitalize';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import {
  StatsAboutRetrievalJobsWaitingForExport,
  StatsAboutRetrievalJobs,
} from 'components/CardStatusSection/CardStatusSection';
import CardNetworkResponseUploadModal from 'scenes/NetworkHealth/scenes/CardNetworkOnboarding/sections/CardNetworkResponseUploadModal';
import FormSelect from 'components/FormSelect';
import LinkConfirm from 'components/LinkConfirm';

import styles from './CardNetworkStatusCards.styles';

type CardStatus = {
  cardNetwork: string;
  logo: string;
  lastAuth: string;
  lastClear: string;
  percentDropped: number;
  percentDroppedTrend: string;
  venuesTransacting: number;
  venuesWithTransactions: number;
  venues: number;
};

export type CardNetworkStatus = {
  VISA: CardStatus;
  MASTERCARD: CardStatus;
  AMEX: CardStatus;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  statsAboutRetrievalJobs: StatsAboutRetrievalJobsWaitingForExport;
  showActions: boolean;
  cardNetworkStatus: {
    VISA: CardStatus;
    MASTERCARD: CardStatus;
    AMEX: CardStatus;
  };
};

const convertToLocalTime = (date: any) => {
  if (!date) return date;
  return moment(date).local().toDate();
};

const renderInfoRow = (
  label: string,
  value: string | number,
  classes: Styles,
  Component: any,
  options: Record<string, unknown>,
  icon?: string,
  signed?: string,
) => (
  <>
    <Grid.Column className={classes.label}>{label}</Grid.Column>
    <Grid.Column className={classes.value}>
      {icon && (
        //@ts-expect-error Icon
        <Icon name={`arrow ${icon}`} />
      )}
      {value && <Component value={value} {...options} />}
      {signed}
    </Grid.Column>
  </>
);
const getColor = (
  venuesTransacting: number,
  venuesWithTransactions: number,
  venues: number,
) => {
  if (venuesTransacting === venues && venuesWithTransactions === venues) {
    return 'green';
  }
  if (venuesTransacting === venues) {
    return 'yellow';
  }

  return 'red';
};

const networkStatusIcon = ({
  venuesTransacting,
  venuesWithTransactions,
  venues,
}: CardStatus) => {
  if (!venuesTransacting && !venuesWithTransactions && !venues) {
    return null;
  }
  const color = getColor(venuesTransacting, venuesWithTransactions, venues);

  return <Icon name="circle" color={color} />;
};

const cardNetworkIds: { [cardNetwork: string]: string } = {
  MASTERCARD: 'MIDs',
  AMEX: 'SEs',
};

const options = [
  { key: 1, text: 'Retrieval', value: 'Retrieval' },
  { key: 2, text: 'Activation Dosh', value: 'Activation_Dosh' },
  {
    key: 3,
    text: 'Activation Reward Networks ',
    value: 'Activation_Reward_Networks',
  },
  { key: 4, text: 'Deactivation Dosh', value: 'Deactivation_Dosh' },
  {
    key: 5,
    text: 'Deactivation Reward Networks',
    value: 'Deactivation_Reward_Networks',
  },
];

const cardNetwork = (
  classes: Styles,
  cardStatus: CardStatus,
  cardRetrievalStats: StatsAboutRetrievalJobs,
  showActions: boolean,
  [processType, setProcessType]: [string, Function],
) => {
  const {
    cardNetwork,
    logo,
    lastAuth,
    lastClear,
    percentDropped,
    percentDroppedTrend,
    venuesTransacting,
  } = cardStatus;

  const buttonClasses = classnames(classes.button, {
    [processType.toLowerCase()]: cardNetwork === 'MASTERCARD',
  });

  const finalProcessType =
    cardNetwork !== 'MASTERCARD' ? 'Retrieval' : processType.split('_')[0];

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setProcessType(value);
  };

  return (
    <Grid.Column className={classes.cnCard}>
      <Card className={classes.card}>
        <Card.Header className={classes.header}>
          <Grid.Row>
            <Grid.Column width={8} floated="left">
              <Image className={classes.icon} src={logo} avatar rounded />

              <span className={classes.cardNetwork}>{cardNetwork}</span>
            </Grid.Column>

            <Grid.Column width={8} floated="right">
              {cardNetwork === 'MASTERCARD' && showActions ? (
                <FormSelect
                  fluid
                  selectOnBlur={false}
                  name="format"
                  placeholder="Format"
                  options={options}
                  onChange={onChange}
                  value={processType}
                  className={classes.formSelect}
                />
              ) : null}
              {networkStatusIcon(cardStatus)}
            </Grid.Column>
          </Grid.Row>
        </Card.Header>

        <Card.Description className={classes.description}>
          <Grid>
            <Grid.Row columns={2}>
              {renderInfoRow(
                'Last Auth',
                convertToLocalTime(lastAuth),
                classes,
                FormattedDate,
                {
                  year: '2-digit',
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                },
              )}

              {renderInfoRow(
                'Last Clear',
                convertToLocalTime(lastClear),
                classes,
                FormattedDate,
                {
                  year: '2-digit',
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                },
              )}

              {renderInfoRow(
                '% Dropped',
                percentDropped,
                classes,
                FormattedNumber,
                { style: 'decimal', maximumFractionDigits: 1 },
                percentDroppedTrend,
                '%',
              )}

              {cardStatus.venuesTransacting >= 0 &&
                renderInfoRow(
                  'Venues',
                  venuesTransacting,
                  classes,
                  FormattedNumber,
                  { style: 'decimal', maximumFractionDigits: 0 },
                )}

              {cardRetrievalStats && (
                <>
                  <Grid.Column className={classes.label}>
                    New Venues
                  </Grid.Column>
                  <Grid.Column className={classes.value}>
                    {cardRetrievalStats.quantity}
                  </Grid.Column>
                </>
              )}
            </Grid.Row>
          </Grid>
        </Card.Description>
      </Card>

      {showActions && (
        <Grid>
          <Grid.Row centered textAlign="center" className={classes.actions}>
            <LinkConfirm
              redirect
              content="We're going to generate the request file. Are you sure?"
              disabled={finalProcessType === 'aggregate'}
              className={buttonClasses}
              to={{
                pathname: '/network-health/cn-onboarding/export-request',
                search: queryString.stringify({
                  cardNetwork,
                  processType: processType.toUpperCase(),
                  limit: 10000,
                }),
              }}
            >
              Request {cardNetworkIds[cardNetwork]}
            </LinkConfirm>

            <CardNetworkResponseUploadModal
              cardNetwork={cardNetwork}
              processType={finalProcessType}
              title={`[${finalProcessType}]: Upload ${capitalize.words(
                cardNetwork,
              )}`}
              trigger={<Button className={buttonClasses}>Upload CNIDs</Button>}
            />

            <Link
              className={buttonClasses}
              to={{
                pathname: '/network-health/cn-onboarding/list-files',
                search: queryString.stringify({
                  cardNetwork,
                  processType: finalProcessType.toUpperCase(),
                }),
              }}
            >
              View Files
            </Link>
          </Grid.Row>
        </Grid>
      )}
    </Grid.Column>
  );
};

const CardNetworkStatus: FunctionComponent<Props> = ({
  classes,
  cardNetworkStatus: { VISA, MASTERCARD, AMEX },
  statsAboutRetrievalJobs: { visaStats, mastercardStats, amexStats },
  showActions,
}) => {
  const processTypePayload = useState<string>('Retrieval');

  return (
    <>
      {cardNetwork(classes, VISA, visaStats, false, processTypePayload)}

      {cardNetwork(
        classes,
        MASTERCARD,
        mastercardStats,
        showActions,
        processTypePayload,
      )}

      {cardNetwork(classes, AMEX, amexStats, false, processTypePayload)}
    </>
  );
};

export default withStyles(styles)(CardNetworkStatus);
